import { getView } from '../engine/prism.js';

/**
 * Sculpt mode types and helpers (the engine owns the strokes; see
 * prism_model schema.md "Sculpting"). `state/model.ts` keeps the live
 * `sculpt` state and re-exports these types.
 */

export type SculptBrushName =
  | 'draw'
  | 'clay'
  | 'crease'
  | 'inflate'
  | 'smooth'
  | 'flatten'
  | 'pinch'
  | 'grab'
  | 'paint'
  | 'carve'
  | 'snakeHook'
  | 'layer'
  | 'clayStrips'
  | 'blob'
  | 'elasticDeform'
  | 'pose'
  | 'boundary'
  | 'mask'
  | 'faceSet'
  | 'hide';

export type FalloffPreset = 'smooth' | 'sphere' | 'root' | 'sharp' | 'linear' | 'constant';
/** Preset name, or `[distance, strength]` points (0..1, monotone cubic between). */
export type Falloff = FalloffPreset | Array<[number, number]>;
export type GestureKind = 'lasso' | 'box' | 'line';

export interface SculptBrush {
  brush: SculptBrushName;
  /** Cursor radius in screen pixels. */
  radius: number;
  strength: number;
  color: [number, number, number];
  /** Mirror across X (sculpts with `symmetry` always mirror their axis). */
  mirror: boolean;
  mirrorY: boolean;
  mirrorZ: boolean;
  /** Sculpt detail multiplier while sculpting (1 = full, 2 = fast). */
  preview: number;
  /** Stored with each stroke; null = the brush's own profile. */
  falloff: Falloff | null;
  /** Stroke sample spacing, % of the radius. */
  spacing: number;
  /** Smooth stroke radius in pixels (0 = off) and catch-up factor. */
  lazy: number;
  lazyFactor: number;
  frontFaces: boolean;
  /** Clay strips plane sampling radius (× brush radius). */
  normalRadius: number;
  /** Layer: keep the height limit across strokes. */
  persistent: boolean;
  /** Mask / hide / face set: draw a gesture instead of painting. */
  gesture: GestureKind | null;
}

export interface SculptState {
  active: boolean;
  path: string | null;
  brush: SculptBrush;
}

export const DEFAULT_SCULPT_BRUSH: SculptBrush = {
  brush: 'draw',
  radius: 40,
  strength: 0.5,
  color: [0.8, 0.35, 0.3],
  mirror: true,
  mirrorY: false,
  mirrorZ: false,
  preview: 2,
  falloff: null,
  spacing: 25,
  lazy: 0,
  lazyFactor: 0.5,
  frontFaces: false,
  normalRadius: 1,
  persistent: false,
  gesture: null,
};

/** Merges an engine `sculpt` event brush (unknown JSON) over the defaults. */
export function parseSculptBrush(raw: unknown, fallback: SculptBrush = DEFAULT_SCULPT_BRUSH): SculptBrush {
  if (!raw || typeof raw !== 'object') return fallback;
  return { ...DEFAULT_SCULPT_BRUSH, ...(raw as Partial<SculptBrush>) };
}

export type BrushGroup = 'Add / remove' | 'Contrast' | 'Transform' | 'Mask & sets';

export interface BrushInfo {
  brush: SculptBrushName;
  label: string;
  group: BrushGroup;
  /** Two-letter glyph shown on the palette button. */
  glyph: string;
  title: string;
}

export const SCULPT_BRUSHES: readonly BrushInfo[] = [
  { brush: 'draw', label: 'Draw', group: 'Add / remove', glyph: 'Dr', title: 'Raise the surface (Ctrl: carve)' },
  { brush: 'clay', label: 'Clay', group: 'Add / remove', glyph: 'Cl', title: 'Lay rounded strips of new clay' },
  { brush: 'clayStrips', label: 'Clay Strips', group: 'Add / remove', glyph: 'CS', title: 'Square flat strips pressed onto the surface plane (Ctrl: dig)' },
  { brush: 'layer', label: 'Layer', group: 'Add / remove', glyph: 'La', title: 'Raise a plateau of limited height (Ctrl: lower)' },
  { brush: 'inflate', label: 'Inflate', group: 'Add / remove', glyph: 'In', title: 'Puff up (Ctrl: deflate)' },
  { brush: 'blob', label: 'Blob', group: 'Add / remove', glyph: 'Bl', title: 'Round blobs: raise and magnify' },
  { brush: 'crease', label: 'Crease', group: 'Add / remove', glyph: 'Cr', title: 'Sharp grooves: wrinkles, folds, lids (Ctrl: ridge)' },
  { brush: 'carve', label: 'Carve', group: 'Add / remove', glyph: 'Ca', title: 'Lower the surface' },
  { brush: 'smooth', label: 'Smooth', group: 'Contrast', glyph: 'Sm', title: 'Relax the surface (or hold Shift with any brush)' },
  { brush: 'flatten', label: 'Flatten', group: 'Contrast', glyph: 'Fl', title: 'Press toward a plane' },
  { brush: 'pinch', label: 'Pinch', group: 'Contrast', glyph: 'Pi', title: 'Pull toward the stroke: sharpen ridges' },
  { brush: 'grab', label: 'Grab', group: 'Transform', glyph: 'Gr', title: 'Drag a region of the surface' },
  { brush: 'elasticDeform', label: 'Elastic', group: 'Transform', glyph: 'El', title: 'Grab with volume-preserving elastic falloff' },
  { brush: 'snakeHook', label: 'Snake Hook', group: 'Transform', glyph: 'SH', title: 'Pull out horns and tentacles along the stroke' },
  { brush: 'pose', label: 'Pose', group: 'Transform', glyph: 'Po', title: 'Rotate the limb under the brush about its base' },
  { brush: 'boundary', label: 'Boundary', group: 'Transform', glyph: 'Bo', title: 'Deform the nearest open border (drag; Ctrl: shrink)' },
  { brush: 'paint', label: 'Paint', group: 'Mask & sets', glyph: 'Pa', title: 'Paint vertex color' },
  { brush: 'mask', label: 'Mask', group: 'Mask & sets', glyph: 'Ma', title: 'Paint the mask: masked surface is protected (Ctrl: erase, Shift: smooth)' },
  { brush: 'faceSet', label: 'Face Sets', group: 'Mask & sets', glyph: 'FS', title: 'Paint a new face set' },
  { brush: 'hide', label: 'Hide', group: 'Mask & sets', glyph: 'Hi', title: 'Hide faces with a gesture (Ctrl: show)' },
];

export const BRUSH_GROUPS: readonly BrushGroup[] = ['Add / remove', 'Contrast', 'Transform', 'Mask & sets'];

/** Brushes that can draw lasso / box / line gestures. */
export function gestureBrush(brush: SculptBrushName): boolean {
  return brush === 'mask' || brush === 'hide' || brush === 'faceSet';
}

/* ------------------------------- falloff ------------------------------- */

export const FALLOFF_PRESETS: readonly FalloffPreset[] = ['smooth', 'sphere', 'root', 'sharp', 'linear', 'constant'];

/** The original brush profile (1 - t²)³, used when no falloff is set. */
export function legacyFalloff(t: number): number {
  if (t >= 1) return 0;
  const u = 1 - t * t;
  return u * u * u;
}

function presetFalloff(p: FalloffPreset, t: number): number {
  const u = 1 - t;
  switch (p) {
    case 'smooth':
      return u * u * (3 - 2 * u);
    case 'sphere':
      return Math.sqrt(Math.max(0, 1 - t * t));
    case 'root':
      return Math.sqrt(u);
    case 'sharp':
      return u * u;
    case 'linear':
      return u;
    case 'constant':
      return 1;
  }
}

/** Sorted, clamped, de-duplicated curve points (as the engine reads them). */
export function normalizeCurve(points: ReadonlyArray<readonly [number, number]>): Array<[number, number]> {
  const clamp = (v: number) => Math.min(1, Math.max(0, v));
  const sorted = points
    .filter(([x, y]) => Number.isFinite(x) && Number.isFinite(y))
    .map(([x, y]) => [clamp(x), clamp(y)] as [number, number])
    .sort((a, b) => a[0] - b[0]);
  const out: Array<[number, number]> = [];
  for (const p of sorted) {
    const last = out[out.length - 1];
    if (!last || Math.abs(p[0] - last[0]) >= 1e-6) out.push(p);
  }
  return out;
}

/** Fritsch–Carlson tangents (no overshoot), same as prism_model. */
export function monotoneTangents(p: ReadonlyArray<readonly [number, number]>): number[] {
  const n = p.length;
  if (n < 2) return new Array(n).fill(0);
  const d: number[] = [];
  for (let k = 0; k < n - 1; k++) d.push((p[k + 1]![1] - p[k]![1]) / Math.max(1e-6, p[k + 1]![0] - p[k]![0]));
  const m = new Array<number>(n).fill(0);
  m[0] = d[0]!;
  m[n - 1] = d[n - 2]!;
  for (let k = 1; k < n - 1; k++) m[k] = d[k - 1]! * d[k]! <= 0 ? 0 : (d[k - 1]! + d[k]!) / 2;
  for (let k = 0; k < n - 1; k++) {
    if (d[k] === 0) {
      m[k] = 0;
      m[k + 1] = 0;
      continue;
    }
    const a = m[k]! / d[k]!;
    const b = m[k + 1]! / d[k]!;
    const s = a * a + b * b;
    if (s > 9) {
      const tau = 3 / Math.sqrt(s);
      m[k] = tau * a * d[k]!;
      m[k + 1] = tau * b * d[k]!;
    }
  }
  return m;
}

function hermite(p: ReadonlyArray<readonly [number, number]>, m: readonly number[], x: number): number {
  const n = p.length;
  if (n === 1 || x <= p[0]![0]) return p[0]![1];
  if (x >= p[n - 1]![0]) return p[n - 1]![1];
  let k = 0;
  while (k < n - 2 && p[k + 1]![0] <= x) k++;
  const h = Math.max(1e-6, p[k + 1]![0] - p[k]![0]);
  const t = (x - p[k]![0]) / h;
  const t2 = t * t;
  const t3 = t2 * t;
  return (
    (2 * t3 - 3 * t2 + 1) * p[k]![1] + (t3 - 2 * t2 + t) * h * m[k]! + (-2 * t3 + 3 * t2) * p[k + 1]![1] + (t3 - t2) * h * m[k + 1]!
  );
}

/** Brush strength at `t` = distance / radius (0 beyond the radius), as the engine computes it. */
export function evalFalloff(falloff: Falloff | null | undefined, t: number): number {
  if (!(t < 1)) return 0;
  if (falloff == null) return legacyFalloff(t);
  const x = Math.max(0, t);
  if (typeof falloff === 'string') return presetFalloff(falloff, x);
  const pts = normalizeCurve(falloff);
  if (pts.length === 0) return legacyFalloff(t);
  return Math.min(1, Math.max(0, hermite(pts, monotoneTangents(pts), x)));
}

/** Editable points for a falloff (presets sampled so they can be reshaped). */
export function curvePoints(falloff: Falloff | null): Array<[number, number]> {
  if (Array.isArray(falloff)) return normalizeCurve(falloff);
  const xs = [0, 0.25, 0.5, 0.75, 1];
  return xs.map((x) => [x, x >= 1 ? (falloff === 'constant' ? 1 : 0) : Math.round(evalFalloff(falloff, x) * 1000) / 1000]);
}

/* ------------------------------- commands ------------------------------ */

/** Runs a sculpt `modelOp` (mask, hide, face sets, dyntopo, multires...). */
export function sculptOp(name: string, params: Record<string, unknown> = {}): void {
  getView('model')?.modelCommand({ op: 'modelOp', name, ...params });
}

/** Sculpt-relevant settings of a part: dyntopo detail and its multires op. */
export interface SculptPartInfo {
  kind: 'shape' | 'op' | 'none';
  detail: number;
  multires: { levels: number; sculptLevel?: number; viewportLevels?: number } | null;
}

export function sculptPartInfo(part: unknown): SculptPartInfo {
  const p = (part ?? {}) as { shape?: { type?: string; dyntopo?: number }; ops?: Array<Record<string, unknown>> };
  const ops = Array.isArray(p.ops) ? p.ops : [];
  const mr = ops.find((o) => o.op === 'multires');
  const multires = mr
    ? {
        levels: typeof mr.levels === 'number' ? mr.levels : 1,
        sculptLevel: typeof mr.sculptLevel === 'number' ? mr.sculptLevel : undefined,
        viewportLevels: typeof mr.viewportLevels === 'number' ? mr.viewportLevels : undefined,
      }
    : null;
  if (p.shape?.type === 'sculpt') return { kind: 'shape', detail: Number(p.shape.dyntopo ?? 0) || 0, multires };
  const last = ops[ops.length - 1];
  if (last?.op === 'sculpt') return { kind: 'op', detail: Number(last.detail ?? 0) || 0, multires };
  return { kind: 'none', detail: 0, multires };
}
